import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface GroupRules {
  goldenPoint: boolean;
  maxGamesPerSet: number;
  setsPerMatch: number;
  superTieBreak: boolean;
}

interface TimingRules {
  warmupMinutes: number;
  restMinutes: number;
  sideChangeSeconds: number;
  rules: string[];
}

interface ConductRules {
  toleranceMinutes: number;
  rules: string[];
}

interface ClassificationRules {
  firstPlaces: number;
  rules: string[];
}

interface AdditionalRules {
  timing: TimingRules;
  conduct: ConductRules;
  classification: ClassificationRules;
}

export interface EventRules {
  groupRules: GroupRules;
  additionalRules: AdditionalRules;
}

export const useEventRules = (eventId: string) => {
  const [rules, setRules] = useState<EventRules | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRules = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('events')
          .select('rules, group_rules, additional_rules')
          .eq('id', eventId)
          .single();

        if (fetchError) throw fetchError;

        // Default values if not configured
        const defaultGroupRules: GroupRules = {
          goldenPoint: false,
          maxGamesPerSet: 6,
          setsPerMatch: 3,
          superTieBreak: false,
        };

        const defaultAdditionalRules: AdditionalRules = {
          timing: {
            warmupMinutes: 5,
            restMinutes: 1,
            sideChangeSeconds: 2,
            rules: [],
          },
          conduct: {
            toleranceMinutes: 15,
            rules: ['Respeto hacia árbitros y rivales', 'Vestimenta deportiva apropiada'],
          },
          classification: {
            firstPlaces: 2,
            rules: ['En caso de empate: diferencia de sets, después diferencia de juegos'],
          },
        };

        // Parse the 'rules' text/JSON column (organizer saves config here)
        let parsedRulesGroupRules: Partial<GroupRules> | null = null;
        if (data?.rules) {
          try {
            const parsed = typeof data.rules === 'string' ? JSON.parse(data.rules) : data.rules;
            if (parsed?.groupRules) {
              parsedRulesGroupRules = parsed.groupRules as Partial<GroupRules>;
            }
          } catch (e) {
            console.warn('Could not parse rules column:', e);
          }
        }

        // Merge: prioritize 'rules.groupRules' over 'group_rules' column
        const groupRulesFromColumn = (data?.group_rules as unknown as GroupRules) || defaultGroupRules;
        const mergedGroupRules: GroupRules = {
          ...defaultGroupRules,
          ...groupRulesFromColumn,
          ...(parsedRulesGroupRules || {}),
        };

        setRules({
          groupRules: mergedGroupRules,
          additionalRules: (data?.additional_rules as unknown as AdditionalRules) || defaultAdditionalRules,
        });
      } catch (err) {
        console.error('Error fetching event rules:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar las reglas');
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchRules();
    }
  }, [eventId]);

  return { rules, loading, error };
};
